import { useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Phone, Mail, MapPin, Clock } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Motion";
import { Seo } from "../components/Seo";
import { trackEvent } from "../components/Analytics";
import { postContact, postAppointment } from "../lib/api";

const field = "w-full border border-border bg-background px-4 py-3.5 text-sm focus:border-accent focus:outline-none";
const times = ["9:00 AM", "10:00 AM", "11:00 AM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"];

export default function Contact() {
  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
  const [cLoading, setCLoading] = useState(false);

  const [appt, setAppt] = useState({ name: "", email: "", phone: "", time: "", topic: "Property management consultation" });
  const [date, setDate] = useState();
  const [aLoading, setALoading] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    setCLoading(true);
    try {
      await postContact({ ...contact, source: "contact" });
      toast.success("Message sent — we'll reply within one business day.");
      trackEvent("generate_lead", { form: "contact", source: "contact" });
      setContact({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setCLoading(false); }
  };

  const submitAppt = async (e) => {
    e.preventDefault();
    if (!date || !appt.time) { toast.error("Please choose a date and time."); return; }
    setALoading(true);
    try {
      await postAppointment({ ...appt, date: format(date, "PPP") });
      toast.success("Consultation booked — confirmation sent to your email.");
      trackEvent("book_appointment", { form: "appointment" });
      setAppt({ name: "", email: "", phone: "", time: "", topic: "Property management consultation" });
      setDate(undefined);
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setALoading(false); }
  };

  return (
    <>
      <Seo title="Contact & Book a Consultation" description="Get in touch with Cummings & Co. Property Management or schedule a free consultation for your Massachusetts rental property." />
      <PageHero
        overline="Let's talk"
        titleLines={["Get in touch.", "Book a call."]}
        subtitle="Whether you have one unit or a portfolio, we'd love to hear about your goals. Reach out or schedule a free consultation below."
      />

      {/* CONTACT INFO STRIP */}
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 divide-y divide-border border-x border-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          {[
            { icon: Phone, l: "Call us", v: "(857) 506-3391" },
            { icon: Mail, l: "Email", v: "hello@cummingsandcopm.com" },
            { icon: MapPin, l: "Office", v: "Boston, Massachusetts" },
          ].map((c, i) => (
            <Reveal key={i} delay={i * 0.08} className="flex items-center gap-4 px-6 py-8 lg:px-10">
              <c.icon className="h-6 w-6 text-accent" strokeWidth={1.4} />
              <div>
                <div className="overline text-muted-foreground">{c.l}</div>
                <div className="mt-1 font-serif text-xl">{c.v}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FORMS */}
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-[1400px] gap-px border-x border-border bg-border lg:grid-cols-2">
          {/* CONTACT FORM */}
          <div className="bg-background p-8 lg:p-14">
            <span className="overline text-accent">Send a message</span>
            <h2 className="mt-4 font-serif text-3xl font-light lg:text-4xl">Tell us about your property</h2>
            <form onSubmit={submitContact} className="mt-8 space-y-4" data-testid="contact-form">
              <input className={field} placeholder="Full name" required data-testid="contact-name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
              <input className={field} type="email" placeholder="Email" required data-testid="contact-email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
              <input className={field} placeholder="Phone (optional)" data-testid="contact-phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
              <textarea className={field + " min-h-[130px] resize-none"} placeholder="How can we help?" required data-testid="contact-message" value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} />
              <button type="submit" disabled={cLoading} data-testid="contact-submit"
                className="w-full bg-primary px-6 py-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent disabled:opacity-60">
                {cLoading ? "Sending…" : "Send Message"}
              </button>
            </form>
          </div>

          {/* APPOINTMENT SCHEDULER */}
          <div className="bg-primary p-8 text-primary-foreground lg:p-14">
            <span className="overline text-accent">Schedule a consultation</span>
            <h2 className="mt-4 font-serif text-3xl font-light lg:text-4xl">Book a free 30-min call</h2>
            <form onSubmit={submitAppt} className="mt-8 space-y-4" data-testid="appointment-form">
              <input className={field + " text-foreground"} placeholder="Full name" required data-testid="appt-name" value={appt.name} onChange={(e) => setAppt({ ...appt, name: e.target.value })} />
              <input className={field + " text-foreground"} type="email" placeholder="Email" required data-testid="appt-email" value={appt.email} onChange={(e) => setAppt({ ...appt, email: e.target.value })} />
              <input className={field + " text-foreground"} placeholder="Phone (optional)" data-testid="appt-phone" value={appt.phone} onChange={(e) => setAppt({ ...appt, phone: e.target.value })} />

              <div className="grid gap-4 sm:grid-cols-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="appt-date-trigger"
                      className={field + " flex items-center gap-2 text-left " + (date ? "text-foreground" : "text-muted-foreground")}>
                      <CalendarIcon className="h-4 w-4" />
                      {date ? format(date, "PPP") : "Pick a date"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto rounded-none border-border p-0" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                  </PopoverContent>
                </Popover>

                <Select value={appt.time} onValueChange={(v) => setAppt({ ...appt, time: v })}>
                  <SelectTrigger className="rounded-none border-border bg-background py-6 text-foreground" data-testid="appt-time">
                    <span className="flex items-center gap-2"><Clock className="h-4 w-4" /><SelectValue placeholder="Time" /></span>
                  </SelectTrigger>
                  <SelectContent>{times.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <button type="submit" disabled={aLoading} data-testid="appt-submit"
                className="w-full bg-accent px-6 py-4 text-sm font-medium text-accent-foreground transition-opacity hover:opacity-90 disabled:opacity-60">
                {aLoading ? "Booking…" : "Confirm Appointment"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
